"use client";

import { useEffect } from "react";

/**
 * Smooth scrolling for deliberate in-page anchor jumps (header menu links, hero
 * CTAs, skip link) — without making it global.
 *
 * `scroll-behavior: smooth` must not live permanently on <html>: that element is
 * also the scroll-snap container (see globals.css), and a smooth scroll-behavior
 * turns every proximity snap into a slow animated scroll that competes with the
 * user's next wheel/trackpad input. The page then feels like it "sticks" in a
 * section for a moment before letting go.
 *
 * Instead we opt into smooth scrolling for one anchor-triggered jump at a time:
 * a capture-phase click listener adds `nav-smooth` to <html> just before the
 * browser/Next performs the hash scroll, then removes it once that scroll
 * settles (`scrollend`, with a timeout fallback for browsers without it).
 */
export default function SmoothAnchors() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const root = document.documentElement;
    let timer = 0;

    const clear = () => {
      window.clearTimeout(timer);
      window.removeEventListener("scrollend", clear);
      root.classList.remove("nav-smooth");
    };

    const onClick = (e: MouseEvent) => {
      // Ignore non-primary / modified clicks (new tab, etc.) and prevented ones.
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      )
        return;

      const a = (e.target as Element | null)?.closest?.<HTMLAnchorElement>(
        "a[href]"
      );
      if (!a || a.target === "_blank") return;

      const url = new URL(a.href, location.href);
      // Same document + a hash → an in-page jump we want to glide into.
      if (url.pathname !== location.pathname || !url.hash) return;

      root.classList.add("nav-smooth");
      window.clearTimeout(timer);
      timer = window.setTimeout(clear, 1000);
      // Identical listener+options is de-duped by the browser, so a rapid second
      // click before the first scroll settles won't stack handlers.
      window.addEventListener("scrollend", clear);
    };

    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      clear();
    };
  }, []);

  return null;
}
